"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import { sorted } from "@/lib/stats/descriptive";
import {
  formatNumberList,
  formatPercent,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  exclusiveRange,
  type PercentileMethod,
  type PercentileRank,
  type PercentileValue,
  percentileRank,
  percentileValue,
} from "@/lib/tool-kit/stats-descriptive/percentiles";

/** English strings. A translated content file overrides these through `ui`. */
export const PERCENTILE_CALCULATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "55, 61, 64, 67, 70, 72, 72, 75",
  valuesDetected: "one: value detected|other: values detected",
  modeLabel: "What do you want to find?",
  modeValue: "The value at a percentile",
  modeRank: "The percentile rank of a value",
  percentLabel: "Percentile P (0–100)",
  percentPlaceholder: "90",
  methodLabel: "Method",
  methodNearest: "Nearest rank",
  methodInclusive: "Inclusive (Excel PERCENTILE.INC)",
  methodExclusive: "Exclusive (Excel PERCENTILE.EXC)",
  scoreLabel: "Value x",
  scorePlaceholder: "80",
  tryExample: "Try an example",
  exampleData:
    "55, 61, 64, 67, 70, 72, 72, 75, 77, 78, 80, 81, 83, 85, 86, 88, 90, 92, 95, 98",
  examplePercent: "90",
  exampleScore: "80",
  clear: "Clear",
  enterData: "Enter at least two numbers.",
  enterPercent: "Enter a percentile between 0 and 100.",
  enterScore: "Enter the value whose percentile rank you want.",
  results: "Results",
  valueAt: "Value at percentile {percent}",
  sampleSize: "Values (n)",
  rankOrPosition: "Rank or position",
  outOfRange:
    "With n = {n}, the exclusive method only reaches percentiles {low} to {high} — Excel's PERCENTILE.EXC returns #NUM! outside that range. Choose another method.",
  workingTitle: "How it was calculated",
  sortedLine: "Sorted data (n = {n}): {list}",
  listSeparator: ", ",
  moreValues: "… and {count} more",
  nearestWorking:
    "Rank = ⌈P ÷ 100 × n⌉ = ⌈{percent} ÷ 100 × {n}⌉ = ⌈{raw}⌉ = {rank} → value number {rank} = {value}",
  inclusiveWorking:
    "Position = 1 + (n − 1) × P ÷ 100 = 1 + {nMinus} × {fraction100} = {position}",
  exclusiveWorking:
    "Position = (n + 1) × P ÷ 100 = {nPlus} × {fraction100} = {position}",
  interpolation:
    "Between value number {rank} ({lower}) and the next ({upper}): {lower} + {fraction} × ({upper} − {lower}) = {value}",
  exactPosition:
    "The position is a whole number, so P is value number {rank}: {value}",
  compareTitle: "The same percentile by each method",
  compareMethod: "Method",
  comparePosition: "Rank or position",
  compareValue: "Value",
  notAvailable: "#NUM!",
  percentBelow: "Below x",
  percentAtOrBelow: "At or below x",
  percentMidRank: "Mid-rank percentile",
  countsLine:
    "B = number of values below {x} = {below}; E = number equal to {x} = {equal}; n = {n}",
  belowWorking: "Below x = B ÷ n × 100 = {below} ÷ {n} × 100 = {result}",
  atOrBelowWorking:
    "At or below x = (B + E) ÷ n × 100 = ({below} + {equal}) ÷ {n} × 100 = {result}",
  midRankWorking:
    "Mid-rank = (B + 0.5 × E) ÷ n × 100 = ({below} + 0.5 × {equal}) ÷ {n} × 100 = {result}",
  rankNote:
    "The three definitions agree only when x is not in the data. When it is, report which one you used.",
} as const;

type Strings = typeof PERCENTILE_CALCULATOR_STRINGS;
type Mode = "value" | "rank";

const METHODS: readonly PercentileMethod[] = [
  "nearest-rank",
  "inclusive",
  "exclusive",
];

function methodLabel(method: PercentileMethod, strings: Strings): string {
  if (method === "inclusive") {
    return strings.methodInclusive;
  }
  if (method === "exclusive") {
    return strings.methodExclusive;
  }
  return strings.methodNearest;
}

function positionWorking(
  result: PercentileValue,
  n: number,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const num = (value: number) => formatStat(fmt, value);
  if (result.method === "nearest-rank") {
    return t(strings.nearestWorking, {
      percent: num(result.percent),
      n: fmt(n),
      raw: num((result.percent * n) / 100),
      rank: fmt(result.position),
      value: num(result.value as number),
    });
  }
  const values = {
    nMinus: fmt(n - 1),
    nPlus: fmt(n + 1),
    fraction100: num(result.percent / 100),
    position: num(result.position),
  };
  return result.method === "exclusive"
    ? t(strings.exclusiveWorking, values)
    : t(strings.inclusiveWorking, values);
}

function ValueWorking({
  result,
  n,
  fmt,
  strings,
}: {
  result: PercentileValue;
  n: number;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const step = result.step;
  const num = (value: number) => formatStat(fmt, value);
  return (
    <>
      <Formula>{positionWorking(result, n, fmt, strings)}</Formula>
      {step && step.fraction > 0 ? (
        <Formula>
          {t(strings.interpolation, {
            rank: fmt(step.lowerRank),
            lower: num(step.lowerValue),
            upper: num(step.upperValue),
            fraction: num(step.fraction),
            value: num(step.value),
          })}
        </Formula>
      ) : null}
      {step && step.fraction === 0 ? (
        <Formula>
          {t(strings.exactPosition, {
            rank: fmt(step.lowerRank),
            value: num(step.value),
          })}
        </Formula>
      ) : null}
    </>
  );
}

function RankResults({
  rank,
  x,
  fmt,
  strings,
}: {
  rank: PercentileRank;
  x: number;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const counts = {
    below: fmt(rank.below),
    equal: fmt(rank.equal),
    n: fmt(rank.n),
    x: formatStat(fmt, x),
  };
  const pct = (value: number) => formatPercent(fmt, value / 100);
  return (
    <>
      <div aria-live="polite">
        <StatGrid>
          <StatCard label={strings.percentBelow} value={pct(rank.strict)} />
          <StatCard label={strings.percentAtOrBelow} value={pct(rank.weak)} />
          <StatCard
            emphasis
            label={strings.percentMidRank}
            value={pct(rank.mean)}
          />
        </StatGrid>
      </div>
      <WorkingPanel title={strings.workingTitle}>
        <Formula>{t(strings.countsLine, counts)}</Formula>
        <Formula>
          {t(strings.belowWorking, { ...counts, result: pct(rank.strict) })}
        </Formula>
        <Formula>
          {t(strings.atOrBelowWorking, { ...counts, result: pct(rank.weak) })}
        </Formula>
        <Formula>
          {t(strings.midRankWorking, { ...counts, result: pct(rank.mean) })}
        </Formula>
        <Explanation>{strings.rankNote}</Explanation>
      </WorkingPanel>
    </>
  );
}

export default function PercentileCalculatorWidget() {
  const strings = useToolUi(PERCENTILE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [mode, setMode] = useState<Mode>("value");
  const [percentText, setPercentText] = useState("");
  const [scoreText, setScoreText] = useState("");
  const [method, setMethod] = useState<PercentileMethod>("inclusive");

  const values = parseNumberList(raw);
  const data = sorted(values);
  const n = data.length;
  const percent = parseNumberInput(percentText);
  const score = parseNumberInput(scoreText);
  const validPercent = percent !== null && percent >= 0 && percent <= 100;
  const result =
    n >= 2 && validPercent ? percentileValue(data, percent, method) : null;
  const rank = n >= 2 && score !== null ? percentileRank(data, score) : null;
  const range = exclusiveRange(n);
  const num = (value: number) => formatStat(fmt, value);

  function fillExample() {
    setRaw(strings.exampleData);
    setPercentText(strings.examplePercent);
    setScoreText(strings.exampleScore);
  }

  function prompt(): string {
    if (n < 2) {
      return strings.enterData;
    }
    return mode === "value" ? strings.enterPercent : strings.enterScore;
  }

  const compareRows = validPercent
    ? METHODS.map((each) => {
        const row = percentileValue(data, percent, each);
        const value = row?.value ?? null;
        return [
          methodLabel(each, strings),
          row ? num(row.position) : "",
          value === null ? strings.notAvailable : num(value),
        ];
      })
    : [];

  const ready = mode === "value" ? result !== null : rank !== null;

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={
            <>
              {fmt(n)}
              {tPlural(strings.valuesDetected, locale, n)}
            </>
          }
          id="pc-data"
          label={strings.dataLabel}
          onChange={setRaw}
          placeholder={strings.dataPlaceholder}
          value={raw}
        />
        <PillGroup
          label={strings.modeLabel}
          name="pc-mode"
          onChange={setMode}
          options={[
            { value: "value", label: strings.modeValue },
            { value: "rank", label: strings.modeRank },
          ]}
          value={mode}
        />
        {mode === "value" ? (
          <>
            <div className="max-w-xs">
              <TextField
                id="pc-percent"
                label={strings.percentLabel}
                numeric
                onChange={setPercentText}
                placeholder={strings.percentPlaceholder}
                value={percentText}
              />
            </div>
            <PillGroup
              label={strings.methodLabel}
              name="pc-method"
              onChange={setMethod}
              options={METHODS.map((value) => ({
                value,
                label: methodLabel(value, strings),
              }))}
              value={method}
            />
          </>
        ) : (
          <div className="max-w-xs">
            <TextField
              id="pc-score"
              label={strings.scoreLabel}
              numeric
              onChange={setScoreText}
              placeholder={strings.scorePlaceholder}
              value={scoreText}
            />
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={fillExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setRaw("");
              setPercentText("");
              setScoreText("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {ready ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {mode === "rank" && rank && score !== null ? (
            <RankResults fmt={fmt} rank={rank} strings={strings} x={score} />
          ) : null}
          {mode === "value" && result && result.value === null ? (
            <ErrorNotice>
              {t(strings.outOfRange, {
                n: fmt(n),
                low: num(range.low),
                high: num(range.high),
              })}
            </ErrorNotice>
          ) : null}
          {mode === "value" && result && result.value !== null ? (
            <>
              <div aria-live="polite">
                <StatGrid>
                  <StatCard
                    emphasis
                    label={t(strings.valueAt, { percent: num(result.percent) })}
                    value={num(result.value)}
                  />
                  <StatCard
                    label={strings.rankOrPosition}
                    value={num(result.position)}
                  />
                  <StatCard label={strings.sampleSize} value={fmt(n)} />
                </StatGrid>
              </div>
              <WorkingPanel title={strings.workingTitle}>
                <Formula>
                  {t(strings.sortedLine, {
                    n: fmt(n),
                    list: formatNumberList(
                      fmt,
                      data,
                      strings.listSeparator,
                      strings.moreValues
                    ),
                  })}
                </Formula>
                <ValueWorking
                  fmt={fmt}
                  n={n}
                  result={result}
                  strings={strings}
                />
              </WorkingPanel>
            </>
          ) : null}
          {mode === "value" ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.compareTitle}</h3>
              <ResultTable
                head={[
                  strings.compareMethod,
                  strings.comparePosition,
                  strings.compareValue,
                ]}
                rows={compareRows}
                wrapFirstColumn
              />
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{prompt()}</Notice>
      )}
    </>
  );
}
